import type { ScopeRef } from "@enduring-systems/entitlements";
import { scopeKey } from "@enduring-systems/entitlements";
import { useEffect, useRef, useState } from "react";

import {
  getOfferings,
  type OfferingCatalogEntry,
  readScopeState,
  type ScopeStateView,
} from "../entitlements-api";
import { READ_DEBOUNCE_MS } from "../use-effective";

export interface UseOfferingsReturn {
  /** Null until the catalog has answered; an empty array is a real answer. */
  readonly offerings: readonly OfferingCatalogEntry[] | null;
  readonly isLoading: boolean;
  readonly error: string | null;
}

/**
 * The offering catalog, read once.
 *
 * Scope-independent by construction (it is code, published at deploy), so it
 * is loaded at the page and passed down rather than called from each section:
 * two always-mounted callers of one global catalog is two fetches and two
 * loading states for one fact.
 */
export function useOfferings(): UseOfferingsReturn {
  const [offerings, setOfferings] = useState<readonly OfferingCatalogEntry[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    getOfferings()
      .then((next) => {
        if (!active) return;
        setOfferings(next);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (!active) return;
        setError(cause instanceof Error ? cause.message : "Failed to load the offering catalog");
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return { offerings, isLoading, error };
}

export interface UseScopeStateReturn {
  readonly view: ScopeStateView | null;
  readonly isLoading: boolean;
  readonly error: string | null;
}

interface ReadState {
  /** The selection this state describes. State for any other one is not an answer. */
  readonly selection: string;
  readonly view: ScopeStateView | null;
  readonly error: string | null;
  readonly isLoading: boolean;
}

function selectionOf(scope: ScopeRef | null, reloadKey: number): string {
  return `${scope === null ? "none" : scopeKey(scope)}|${reloadKey}`;
}

/**
 * Everything set at one scope, re-read when `reloadKey` changes so a write
 * refreshes the reading it invalidated.
 *
 * Every result is stored WITH the selection it answers and discarded on read
 * if the selection has moved on, which is what makes a value attributed to the
 * wrong workspace impossible rather than unlikely. A request-id counter cannot
 * do that here: it is invalidated in an effect, effects run after paint, and a
 * response landing in that window is accepted against the previous scope. On
 * this page that would paint one customer's entitlements under another
 * customer's name. `use-effective.ts` carries the same reasoning for the
 * per-capability read.
 *
 * The debounce is the raw tab's doing: the scope id there is still free text,
 * so a scope changes on every keystroke, and an intermediate prefix can name a
 * real tenant whose state this read would otherwise pull and audit.
 *
 * `scope` must be referentially stable across renders (the page memoizes it).
 */
export function useScopeState(scope: ScopeRef | null, reloadKey: number): UseScopeStateReturn {
  const selection = selectionOf(scope, reloadKey);
  /* Assigned during RENDER, not in an effect: effects run after paint, so a
     ref updated there still holds the previous selection for one commit, which
     is the window this whole hook exists to close. The assignment is
     idempotent and touches nothing outside the component. */
  const currentSelection = useRef(selection);
  currentSelection.current = selection;

  const [state, setState] = useState<ReadState>({
    selection,
    view: null,
    error: null,
    isLoading: false,
  });

  useEffect(() => {
    if (scope === null) {
      setState({ selection, view: null, error: null, isLoading: false });
      return;
    }

    setState({ selection, view: null, error: null, isLoading: true });
    const timer = setTimeout(() => {
      readScopeState(scope)
        .then((next) => {
          /* A response for an abandoned selection is dropped rather than
             stored. Storing it and filtering at render is not enough: it
             REPLACES the state of the selection now on screen, so a read that
             already answered gets blanked back to a spinner that nothing will
             ever resolve. */
          if (currentSelection.current !== selection) return;
          setState({ selection, view: next, error: null, isLoading: false });
        })
        .catch((cause: unknown) => {
          if (currentSelection.current !== selection) return;
          setState({
            selection,
            view: null,
            error:
              cause instanceof Error ? cause.message : "Failed to read this scope's entitlements",
            isLoading: false,
          });
        });
    }, READ_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [scope, reloadKey, selection]);

  if (state.selection === selection) {
    return { view: state.view, isLoading: state.isLoading, error: state.error };
  }
  // The selection changed and the effect has not run yet: report the read as
  // pending rather than handing back the previous scope's answer.
  return { view: null, error: null, isLoading: scope !== null };
}
